import { useMemo, useState } from "react";
import SectionHeading from "@/components/base/SectionHeading";
import { movies, movieGenres, movieRegions } from "@/mocks/movies";

function Stars({ value }: { value: number }) {
  return (
    <span className="inline-flex items-center gap-0.5 text-primary-500" aria-label={`推荐指数 ${value} 分`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <i key={i} className={i <= value ? "ri-star-fill text-sm" : "ri-star-line text-sm text-background-400"}></i>
      ))}
    </span>
  );
}

function FilterChip({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`px-3.5 py-1.5 rounded-full text-xs md:text-sm whitespace-nowrap transition-colors cursor-pointer border ${
        active
          ? "bg-primary-500 text-background-50 border-primary-500"
          : "bg-background-100 text-foreground-700 border-background-200 hover:border-primary-300 hover:text-primary-700"
      }`}
    >
      {label}
    </button>
  );
}

export default function MovieExplorer() {
  const [region, setRegion] = useState("全部");
  const [genre, setGenre] = useState("全部");
  const [query, setQuery] = useState("");

  const result = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    return movies.filter((m) => {
      const regionOk = region === "全部" || m.region === region;
      const genreOk = genre === "全部" || m.genres.includes(genre);
      const queryOk =
        keyword.length === 0 ||
        m.title.toLowerCase().includes(keyword) ||
        m.original.toLowerCase().includes(keyword);
      return regionOk && genreOk && queryOk;
    });
  }, [region, genre, query]);

  return (
    <section className="w-full px-4 md:px-6 py-16 md:py-24 bg-background-100">
      <SectionHeading
        id="movies"
        eyebrow="Latest Films"
        title="最新电影：按地区与类型快速筛选"
        description="把关注度较高的影片按地区与类型整理在一起。你可以直接用下方的筛选条件和搜索框缩小范围，结果会实时更新，方便对照后决定先看哪一部。"
      />

      <div className="mt-8 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="flex-1 flex flex-wrap items-center gap-2">
            <span className="text-xs text-foreground-500 mr-1 inline-flex items-center gap-1">
              <i className="ri-map-pin-line"></i>
              地区
            </span>
            {movieRegions.map((r) => (
              <FilterChip key={r} label={r} active={region === r} onClick={() => setRegion(r)} />
            ))}
          </div>

          <div className="relative w-full lg:w-64">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜索片名 / 原名"
              aria-label="搜索片名或原名"
              className="w-full pl-9 pr-8 py-2 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
            />
            {query.length > 0 && (
              <button
                type="button"
                aria-label="清空搜索"
                onClick={() => setQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full text-foreground-500 hover:bg-background-200 cursor-pointer"
              >
                <i className="ri-close-line text-sm"></i>
              </button>
            )}
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2 pt-4 border-t border-background-200">
          <span className="text-xs text-foreground-500 mr-1 inline-flex items-center gap-1">
            <i className="ri-price-tag-3-line"></i>
            类型
          </span>
          {movieGenres.map((g) => (
            <FilterChip key={g} label={g} active={genre === g} onClick={() => setGenre(g)} />
          ))}
        </div>
      </div>

      <div className="mt-5 flex items-center justify-between">
        <p className="text-sm text-foreground-600">
          共找到 <span className="font-semibold text-accent-600">{result.length}</span> 部影片
        </p>
        {(region !== "全部" || genre !== "全部" || query.length > 0) && (
          <button
            type="button"
            onClick={() => {
              setRegion("全部");
              setGenre("全部");
              setQuery("");
            }}
            className="text-xs text-foreground-500 hover:text-accent-600 inline-flex items-center gap-1 cursor-pointer"
          >
            <i className="ri-refresh-line"></i>
            重置筛选
          </button>
        )}
      </div>

      {result.length > 0 ? (
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
          {result.map((m) => (
            <article
              key={m.id}
              className="group rounded-lg overflow-hidden border border-background-200 bg-background-50 transition-colors hover:border-accent-300 flex flex-col"
            >
              <div className="w-full h-56 overflow-hidden bg-background-200">
                <img
                  src={m.image}
                  alt={`${m.title} 剧照封面`}
                  title={`${m.title} 剧照封面`}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="p-4 md:p-5 flex flex-col flex-1">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-heading text-base font-semibold text-foreground-950 leading-snug">{m.title}</h3>
                  <Stars value={m.stars} />
                </div>
                <p className="mt-1 text-xs text-foreground-500">{m.original}</p>
                <div className="mt-3 flex flex-wrap items-center gap-1.5">
                  <span className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-800 text-[11px]">
                    {m.year}
                  </span>
                  <span className="px-2 py-0.5 rounded-full bg-background-200 text-foreground-700 text-[11px]">
                    {m.region}
                  </span>
                  {m.genres.map((g) => (
                    <span key={g} className="px-2 py-0.5 rounded-full bg-accent-100 text-accent-800 text-[11px]">
                      {g}
                    </span>
                  ))}
                </div>
                <p className="mt-3 text-sm leading-6 text-foreground-600 flex-1">{m.blurb}</p>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <div className="mt-8 rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
          <span className="w-14 h-14 mx-auto rounded-full bg-background-100 flex items-center justify-center text-foreground-400">
            <i className="ri-search-eye-line text-2xl"></i>
          </span>
          <p className="mt-4 text-sm text-foreground-600">没有符合当前条件的影片</p>
          <button
            type="button"
            onClick={() => {
              setRegion("全部");
              setGenre("全部");
              setQuery("");
            }}
            className="mt-4 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
          >
            重置筛选条件
          </button>
        </div>
      )}
    </section>
  );
}